import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Trash2, Edit3, ChevronDown, ChevronUp, AlertTriangle, Clock, User, X, Loader2, RefreshCw, Globe, Building2, Users, FileText, Zap, RotateCcw, History, CheckCircle2, XCircle, Info } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Tier Badge ── */
const TierBadge = ({ tier }) => {
  const colors = {
    high_fit: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    moderate_fit: 'bg-blue-50 text-blue-700 border-blue-200',
    stretch_risk: 'bg-amber-50 text-amber-700 border-amber-200',
    non_target: 'bg-gray-100 text-gray-600 border-gray-200',
    excluded: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold rounded border ${colors[tier] || 'bg-gray-50 text-gray-500 border-gray-200'}`} data-testid={`tier-badge-${tier}`}>
      {tier?.replace('_', ' ') || 'unknown'}
    </span>
  );
};

/* ── Conflict Badge ── */
const ConflictBadge = ({ conflict }) => {
  const icons = { excluded: XCircle, already_enriched: CheckCircle2, current_tier: Info, not_in_pipeline: AlertTriangle };
  const colors = { excluded: 'text-red-500', already_enriched: 'text-emerald-500', current_tier: 'text-blue-500', not_in_pipeline: 'text-amber-500' };
  const Icon = icons[conflict.type] || Info;
  return (
    <div className="flex items-start gap-1.5 text-[11px]" data-testid={`conflict-${conflict.type}`}>
      <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${colors[conflict.type] || 'text-gray-500'}`} />
      <span className="text-gray-600">{conflict.message}</span>
    </div>
  );
};

/* ── Add/Edit Override Modal ── */
const OverrideModal = ({ override, onSave, onCancel, saving }) => {
  const isEdit = !!override;
  const [form, setForm] = useState({
    company_name: override?.company_name || '',
    domain: override?.domain || '',
    aliases: (override?.aliases || []).join(', '),
    employee_count: override?.employee_count || '',
    industry: override?.industry || '',
    notes: override?.notes || '',
  });
  const [conflicts, setConflicts] = useState(null);
  const [checking, setChecking] = useState(false);

  const checkConflicts = useCallback(async (name) => {
    if (!name.trim()) return;
    setChecking(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/settings/enrichment-overrides/conflicts/${encodeURIComponent(name.trim())}`);
      setConflicts(data.conflicts || []);
    } catch { setConflicts(null); }
    setChecking(false);
  }, []);

  useEffect(() => {
    if (!isEdit && form.company_name.length >= 3) {
      const t = setTimeout(() => checkConflicts(form.company_name), 500);
      return () => clearTimeout(t);
    }
  }, [form.company_name, isEdit, checkConflicts]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error('Company name required'); return; }
    onSave({
      company_name: form.company_name.trim(),
      domain: form.domain.trim(),
      aliases: form.aliases.split(',').map(a => a.trim()).filter(Boolean),
      employee_count: form.employee_count ? parseInt(form.employee_count, 10) : null,
      industry: form.industry.trim(),
      notes: form.notes.trim(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="override-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#0A1F44]">{isEdit ? 'Edit Override' : 'Add Enrichment Override'}</h3>
          <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Company Name *</label>
            <input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
              disabled={isEdit} placeholder="e.g. PNC Bank"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37] disabled:bg-gray-50 disabled:text-gray-500"
              data-testid="override-company-name" />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Domain</label>
            <div className="relative">
              <Globe className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input value={form.domain} onChange={e => setForm(p => ({ ...p, domain: e.target.value }))}
                placeholder="e.g. pnc.com" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                data-testid="override-domain" />
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Providing a domain enables Apollo enrichment by domain lookup</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Employee Count</label>
              <div className="relative">
                <Users className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input type="number" min="0" value={form.employee_count} onChange={e => setForm(p => ({ ...p, employee_count: e.target.value }))}
                  placeholder="e.g. 250" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                  data-testid="override-employee-count" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Industry</label>
              <input value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                placeholder="e.g. Financial Services" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                data-testid="override-industry" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Aliases</label>
            <input value={form.aliases} onChange={e => setForm(p => ({ ...p, aliases: e.target.value }))}
              placeholder="Comma-separated, e.g. PNC Financial, PNC Financial Services"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
              data-testid="override-aliases" />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Why this override is needed..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37] resize-none"
              data-testid="override-notes" />
          </div>

          {/* Conflict warnings */}
          {checking && <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 className="h-3 w-3 animate-spin" /> Checking conflicts...</div>}
          {conflicts && conflicts.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1.5" data-testid="override-conflicts">
              <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">Detected Conflicts</p>
              {conflicts.map((c, i) => <ConflictBadge key={i} conflict={c} />)}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-5 py-2 bg-[#0A1F44] text-white text-xs font-bold rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center gap-1.5"
              data-testid="override-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {isEdit ? 'Update Override' : 'Save Override'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Audit Log Panel ── */
const AuditLogPanel = ({ entries, loading }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="audit-log-panel">
    <div className="p-4 border-b border-gray-100 flex items-center gap-2">
      <History className="h-4 w-4 text-[#0A1F44]/60" />
      <h3 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider">Audit Trail</h3>
    </div>
    <div className="max-h-72 overflow-y-auto">
      {loading ? (
        <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" /></div>
      ) : entries.length === 0 ? (
        <div className="p-6 text-center text-xs text-gray-400">No audit entries yet</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {entries.map((e, i) => (
            <div key={i} className="px-4 py-3 hover:bg-gray-50/50" data-testid={`audit-entry-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-[#0A1F44]">{e.company_name}</span>
                <span className="text-[10px] text-gray-400">{new Date(e.at).toLocaleString()}</span>
              </div>
              <p className="text-[11px] text-gray-600">{e.details}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-[10px] text-gray-400 flex items-center gap-1"><User className="h-3 w-3" />{e.by}</span>
                {e.domain && <span className="text-[10px] text-gray-400 flex items-center gap-1"><Globe className="h-3 w-3" />{e.domain}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

/* ── Override Row ── */
const OverrideRow = ({ entry, onEdit, onDelete, onReEnrich, onReclassify, expanded, onToggle, actionLoading }) => (
  <div className="border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md hover:border-[#D4AF37]/30 transition-all" data-testid={`override-row-${entry.key}`}>
    <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={onToggle}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="h-4 w-4 text-[#0A1F44]/60 shrink-0" />
          <span className="text-sm font-bold text-[#0A1F44] truncate">{entry.company_name}</span>
          {entry.domain && <span className="text-[10px] text-gray-400 flex items-center gap-1 shrink-0"><Globe className="h-3 w-3" />{entry.domain}</span>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {entry.employee_count && (
            <span className="text-[10px] text-gray-500 flex items-center gap-1"><Users className="h-3 w-3" />{entry.employee_count.toLocaleString()} emp</span>
          )}
          {entry.industry && (
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{entry.industry}</span>
          )}
          {entry.aliases?.length > 0 && (
            <span className="text-[10px] text-gray-400">+{entry.aliases.length} alias{entry.aliases.length > 1 ? 'es' : ''}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10px] text-gray-400">{new Date(entry.updated_at).toLocaleDateString()}</span>
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </div>
    </div>

    {expanded && (
      <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
        {/* Data summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400 uppercase">Domain</p>
            <p className="text-xs font-semibold text-[#0A1F44]">{entry.domain || '—'}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400 uppercase">Employees</p>
            <p className="text-xs font-semibold text-[#0A1F44]">{entry.employee_count ? entry.employee_count.toLocaleString() : '—'}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400 uppercase">Industry</p>
            <p className="text-xs font-semibold text-[#0A1F44]">{entry.industry || '—'}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400 uppercase">Source</p>
            <p className="text-xs font-semibold text-[#D4AF37]">Manual Override</p>
          </div>
        </div>

        {entry.aliases?.length > 0 && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase mb-1">Aliases</p>
            <div className="flex flex-wrap gap-1">
              {entry.aliases.map((a, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-full">{a}</span>
              ))}
            </div>
          </div>
        )}

        {entry.notes && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase mb-1">Notes</p>
            <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded-lg">{entry.notes}</p>
          </div>
        )}

        {/* Inline audit history */}
        {entry.audit_history?.length > 0 && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase mb-1">Change History</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {entry.audit_history.slice().reverse().map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-[10px]">
                  <Clock className="h-3 w-3 text-gray-300 mt-0.5 shrink-0" />
                  <span className="text-gray-500">{new Date(a.at).toLocaleString()} — {a.details}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <button onClick={() => onReEnrich(entry.key)} disabled={!entry.domain || actionLoading}
            className="px-3 py-1.5 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
            data-testid={`re-enrich-${entry.key}`}>
            {actionLoading === `re-enrich-${entry.key}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
            Re-enrich
          </button>
          <button onClick={() => onReclassify(entry.key)} disabled={actionLoading}
            className="px-3 py-1.5 text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-40 flex items-center gap-1.5 transition-colors"
            data-testid={`reclassify-${entry.key}`}>
            {actionLoading === `reclassify-${entry.key}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
            Reclassify
          </button>
          <div className="flex-1" />
          <button onClick={() => onEdit(entry)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-testid={`edit-${entry.key}`}>
            <Edit3 className="h-3.5 w-3.5 text-gray-500" />
          </button>
          <button onClick={() => onDelete(entry.key)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-${entry.key}`}>
            <Trash2 className="h-3.5 w-3.5 text-red-400" />
          </button>
        </div>
      </div>
    )}
  </div>
);

/* ── Main Page ── */
export default function CompanyEnrichmentOverrides({ embedded = false }) {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const loadOverrides = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/settings/enrichment-overrides`);
      setOverrides(data.entries || []);
    } catch { toast.error('Failed to load overrides'); }
    setLoading(false);
  }, []);

  const loadAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/settings/enrichment-overrides/audit-log?limit=50`);
      setAuditLog(data.entries || []);
    } catch { /* silent */ }
    setAuditLoading(false);
  }, []);

  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editTarget) {
        await fetchWithAuth(`${API_URL}/api/settings/enrichment-overrides/${encodeURIComponent(editTarget.key)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        toast.success(`Updated override for ${formData.company_name}`);
      } else {
        await fetchWithAuth(`${API_URL}/api/settings/enrichment-overrides`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        toast.success(`Created override for ${formData.company_name}`);
      }
      setShowModal(false);
      setEditTarget(null);
      loadOverrides();
    } catch (err) {
      toast.error(err.message || 'Failed to save override');
    }
    setSaving(false);
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Delete this override? This cannot be undone.')) return;
    try {
      await fetchWithAuth(`${API_URL}/api/settings/enrichment-overrides/${encodeURIComponent(key)}`, { method: 'DELETE' });
      toast.success('Override deleted');
      loadOverrides();
    } catch { toast.error('Failed to delete'); }
  };

  const handleReEnrich = async (key) => {
    setActionLoading(`re-enrich-${key}`);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/settings/enrichment-overrides/${encodeURIComponent(key)}/re-enrich`, { method: 'POST' });
      if (res.success) {
        toast.success(`Re-enriched: ${res.old_tier} → ${res.new_tier} (${res.employee_count?.toLocaleString()} emp, source: ${res.source})`);
        loadOverrides();
      } else {
        toast.error(res.message || 'Re-enrichment returned no data');
      }
    } catch (err) { toast.error(err.message || 'Re-enrich failed'); }
    setActionLoading(null);
  };

  const handleReclassify = async (key) => {
    setActionLoading(`reclassify-${key}`);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/settings/enrichment-overrides/${encodeURIComponent(key)}/reclassify`, { method: 'POST' });
      if (res.success) {
        toast.success(`Reclassified: ${res.old_tier} → ${res.new_tier}`);
        loadOverrides();
      } else {
        toast.error('Reclassification failed');
      }
    } catch (err) { toast.error(err.message || 'Reclassify failed'); }
    setActionLoading(null);
  };

  const filtered = overrides.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return o.company_name.toLowerCase().includes(q) ||
      o.domain?.toLowerCase().includes(q) ||
      o.aliases?.some(a => a.toLowerCase().includes(q));
  });

  return (
    <IntelligenceLayout title="Company Enrichment Overrides" embedded={embedded}>
      <div className="p-6 space-y-5 max-w-5xl" data-testid="enrichment-overrides-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-[#0A1F44]">Company Enrichment Overrides</h1>
            <p className="text-xs text-gray-500 mt-0.5">Manually provide domains, employee counts, and identity data when Apollo enrichment fails</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setShowAudit(!showAudit); if (!showAudit) loadAuditLog(); }}
              className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-colors flex items-center gap-1.5 ${showAudit ? 'bg-[#0A1F44] text-white border-[#0A1F44]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
              data-testid="toggle-audit-btn">
              <History className="h-3.5 w-3.5" /> Audit Log
            </button>
            <button onClick={() => { setEditTarget(null); setShowModal(true); }}
              className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c9a432] transition-colors flex items-center gap-1.5 shadow-sm"
              data-testid="add-override-btn">
              <Plus className="h-3.5 w-3.5" /> Add Override
            </button>
          </div>
        </div>

        {/* Info banner */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2.5" data-testid="info-banner">
          <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
          <div className="text-[11px] text-blue-700 space-y-0.5">
            <p className="font-semibold">How overrides work</p>
            <p>Overrides let you manually supply company identity data (domain, employee count) for companies that Apollo could not match automatically. Use <strong>Re-enrich</strong> to look up a company via its override domain, or <strong>Reclassify</strong> to re-run classification with the override data. Override data does not silently replace system-enriched data — all changes are logged in the audit trail.</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by company, domain, or alias..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37] bg-white"
            data-testid="override-search" />
        </div>

        {/* Audit Log */}
        {showAudit && <AuditLogPanel entries={auditLog} loading={auditLoading} />}

        {/* Override List */}
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-xl" data-testid="empty-state">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">{search ? 'No overrides match your search' : 'No enrichment overrides yet'}</p>
            <p className="text-xs text-gray-400 mt-1">Add one to manually provide company data when Apollo enrichment fails</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{filtered.length} override{filtered.length !== 1 ? 's' : ''}</p>
            {filtered.map(entry => (
              <OverrideRow key={entry.key} entry={entry}
                expanded={expanded === entry.key}
                onToggle={() => setExpanded(expanded === entry.key ? null : entry.key)}
                onEdit={(e) => { setEditTarget(e); setShowModal(true); }}
                onDelete={handleDelete}
                onReEnrich={handleReEnrich}
                onReclassify={handleReclassify}
                actionLoading={actionLoading} />
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <OverrideModal
            override={editTarget}
            onSave={handleSave}
            onCancel={() => { setShowModal(false); setEditTarget(null); }}
            saving={saving} />
        )}
      </div>
    </IntelligenceLayout>
  );
}
